import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createInsightsApiClient } from '../../../lib/apiClient';
import type { ReportIssueType } from '../InsightsPage.types';

type ReportSubmitStatus = 'idle' | 'success' | 'error';

interface UseInsightsReportIssueOptions {
  threadId: string | null;
}

interface UseInsightsReportIssueResult {
  reportIssueMessageId: string | null;
  reportIssueType: ReportIssueType;
  reportIssueDescription: string;
  isSubmittingReport: boolean;
  reportSubmitStatus: ReportSubmitStatus;
  setReportIssueType: (nextType: ReportIssueType) => void;
  setReportIssueDescription: (nextDescription: string) => void;
  openReportIssue: (messageId: string) => void;
  closeReportIssue: () => void;
  submitReport: () => Promise<void>;
}

export const useInsightsReportIssue = ({ threadId }: UseInsightsReportIssueOptions): UseInsightsReportIssueResult => {
  const insightsClient = useMemo(() => createInsightsApiClient(), []);
  const closeTimerRef = useRef<number | null>(null);
  const [reportIssueMessageId, setReportIssueMessageId] = useState<string | null>(null);
  const [reportIssueType, setReportIssueType] = useState<ReportIssueType>('incorrect-response');
  const [reportIssueDescription, setReportIssueDescription] = useState<string>('');
  const [isSubmittingReport, setIsSubmittingReport] = useState<boolean>(false);
  const [reportSubmitStatus, setReportSubmitStatus] = useState<ReportSubmitStatus>('idle');

  const closeReportIssue = useCallback((): void => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }

    setReportIssueMessageId(null);
    setReportIssueDescription('');
    setReportSubmitStatus('idle');
  }, []);

  const openReportIssue = useCallback((messageId: string): void => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }

    setReportIssueMessageId(messageId);
    setReportIssueType('incorrect-response');
    setReportIssueDescription('');
    setReportSubmitStatus('idle');
  }, []);

  const submitReport = useCallback(async (): Promise<void> => {
    if (!threadId || !reportIssueMessageId) {
      return;
    }

    const trimmedDescription = reportIssueDescription.trim();
    if (!trimmedDescription) {
      return;
    }

    setIsSubmittingReport(true);
    setReportSubmitStatus('idle');

    try {
      await insightsClient.post('insights/report-issue', {
        json: {
          threadId,
          messageId: reportIssueMessageId,
          issueType: reportIssueType,
          description: trimmedDescription,
        },
      });

      setReportSubmitStatus('success');
      setReportIssueDescription('');

      closeTimerRef.current = window.setTimeout(() => {
        closeReportIssue();
      }, 2000);
    } catch {
      setReportSubmitStatus('error');
    } finally {
      setIsSubmittingReport(false);
    }
  }, [closeReportIssue, insightsClient, reportIssueDescription, reportIssueMessageId, reportIssueType, threadId]);

  useEffect(() => {
    const onEscape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && reportIssueMessageId) {
        closeReportIssue();
      }
    };

    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('keydown', onEscape);
    };
  }, [closeReportIssue, reportIssueMessageId]);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current !== null) {
        window.clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  return {
    reportIssueMessageId,
    reportIssueType,
    reportIssueDescription,
    isSubmittingReport,
    reportSubmitStatus,
    setReportIssueType,
    setReportIssueDescription,
    openReportIssue,
    closeReportIssue,
    submitReport,
  };
};
